<template>
    <div class="flex flex-col lg:flex-row justify-between items-center">
        <h2 class="text-xl leading-9 font-headline font-medium text-bhav-blue-600">Interactive Media</h2>
        <FormButton v-if="GlobalPolicy.edit()" color="blue" type="button" @click="() => {
            modal = true
        }">
            <span class="fak fa-plus mr-1"></span>Add
        </FormButton>
    </div>
    <PaginationTable empty-message="There are currently no interactive media items." :data="item.meta.interactives" :columns="columns" :view="`${$route.name}-${$route.params.tab}`" :legacy="false">
        <template #col-position="{row}">
            <div class="flex gap-2 items-center py-4">
                <div class="text-sm leading-5 text-oakwood-gray-900 mr-2">{{ item.meta.interactives.indexOf(row) + 1 }}
                </div>
                <button type="button" :disabled="item.meta.interactives.indexOf(row) == 0" @click="move(row, 'up')"
                    :class="{ 'cursor-pointer': item.meta.interactives.indexOf(row) > 0, 'text-oakwood-gray-300 cursor-not-allowed': item.meta.interactives.indexOf(row) == 0 }"><span
                        class="far fa-arrow-up"></span></button>
                <button type="button" :disabled="item.meta.interactives.indexOf(row) == item.meta.interactives.length - 1"
                    @click="move(row, 'down')"
                    :class="item.meta.interactives.indexOf(row) < item.meta.interactives.length - 1 ? 'cursor-pointer' : 'text-oakwood-gray-300 cursor-not-allowed'"><span
                        class="far fa-arrow-down"></span></button>
            </div>
        </template>

        <template #col-title="{row}">
            <a :href="row.url" target="_blank" class="block py-4 text-bhav-blue-600 hover:text-bhav-blue-900">
                {{ row.title }}
            </a>
        </template>

        <template #col-type="{row}">
            <div class="py-4">
                {{ row.type }}
            </div>
        </template>

        <template #col-actions="{row}" v-if="GlobalPolicy.edit()">
            <div class="flex gap-2 justify-end py-4">
                <FormButton type="button" @click="edit(item.meta.interactives.indexOf(row))" icon="fa-pen" color="blue" prefix-class="fak">
                    Edit
                </FormButton>
                <FormButton type="button" @click="openDelete(item.meta.interactives.indexOf(row))" prefix-class="fak" icon="fa-trash" color="red"/>
            </div>
        </template>
    </PaginationTable>

    <ModalDialog :open="modal" :no-padding="true" @close="cancel">
        <template #headers>
            <div>
                <h3 class="text-lg leading-6 font-medium text-bhav-blue-500">
                    Interactive Media Editor
                </h3>
                <p class="mt-1 max-w-2xl text-sm leading-5 text-oakwood-gray-500">
                    Enter the details about an interactive experience you wish to include with this {{ context }}.
                </p>
            </div>
        </template>
        <template #body>
            <div class="mt-6 sm:mt-5">
                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="title"
                        class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2">
                        Title
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <TextInput id="title" v-model="interactive.title" class="w-full" :max-length="50"/>
                    </div>
                </div>

                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="url"
                        class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2">
                        URL
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <URLField id="url" v-model="interactive.url" width="w-full" />
                    </div>
                </div>

                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:pt-5">
                    <label for="type"
                        class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2">
                        Type
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <SelectField id="type" v-model="interactive.type" :options="types.map(type => ({label: type, value: type}))" :allowNull="false" />
                    </div>
                </div>

                <div class="mt-6 sm:mt-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start align-center sm:border-t sm:border-oakwood-gray-200 sm:pt-5">
                    <label for="flash"
                        class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2">
                        Uses Flash
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <label for="sr-flash"  
                            :class="interactive.flash ? 'bg-bhav-blue-600' : 'bg-oakwood-gray-200'"
                            class="bg-oakwood-gray-200 relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:shadow-outline">
                        <input id="sr-flash" tabindex="0" type="checkbox" v-model="interactive.flash" class="sr-only">
                        <span aria-hidden="true" 
                            :class="interactive.flash ? 'translate-x-5' : 'translate-x-0'"
                            class="inline-block h-5 w-5 rounded-full bg-white shadow transform transition ease-in-out duration-200"></span>
                        </label>
                    </div>
                </div>

                
            </div>

        </template>

        <template #buttons>
            <div class="flex justify-end mt-5 pt-5">
                <span class="inline-flex rounded-md shadow-sm">
                    <FormButton type="button" @click="cancel"color="gray">
                        Cancel
                    </FormButton>
                </span>
                <span class="ml-3 inline-flex rounded-md shadow-sm">
                    <FormButton type="button" @click="save" color="blue">
                        Save
                    </FormButton>
                </span>
            </div>
        </template>
    </ModalDialog>
    <ModalDialog v-if="deleting" :no-padding="true" :open="deleting" @close="cancel">
        <template #header>
            <h2 class="text-lg text-oakwood-red-600 font-semibold">Confirm Interactive Media Deletion</h2>
        </template>
        <template #body>
            <p class="text-oakwood-gray-900"><strong>Warning!</strong> You are about to delete <em>{{ item.meta.interactives[index]?.title }}</em>. Are you sure?</p>
        </template>
        <template #buttons>
            <FormButton type="button" @click="cancel" color="gray">
                Cancel
            </FormButton>
            <FormButton type="button" @click="drop" prefix-class="fak" icon="fa-trash" color="red"/>
        </template>
    </ModalDialog>
</template>

<script>
import {FormButton, URLField} from '@/components/form';
import ModalDialog from '@/components/ModalDialog.vue';
import { GlobalPolicy } from '@/policies/global';
import SelectField from '../form/SelectField.vue';
import PaginationTable from '@/components/PaginationTable.vue';
import TextInput from '../form/TextInput.vue';

const interactive = {
        type: '',
        title: '',
        url: '',
        flash: false
    }

const types = [
    'Cutaway',
    'Exterior Changer',
    'Room Changer'
]

const community_types = [
    ...types,
    'Interactive Site Plan',
    'Community Features'
]

const floorplan_types = [
    ...types,
    'Exterior 360 View',
    'Interactive Floorplan',
    'Plan Features',
    'Virtual Walkthrough'
]

export default {
    setup() {
        return {
            GlobalPolicy
        }
    },
    data() {
        return {
            modal: false,
            index: false,
            interactive: {...interactive},
            deleting: false,
            types: ['community','collection'].includes(this.context) ? community_types : floorplan_types
        }
    },
    emits: [
        'update'
    ],
    computed: {
        columns() {
            let cols = [
                {
                    name: 'position',
                    label: 'Position',
                    sort: false,
                    default: true
                },
                {
                    name: 'title',
                    label: 'Title',
                    sort: false,
                    default: true
                },
                {
                    name: 'type',
                    label: 'Type',
                    sort: false,
                    default: true
                }
            ];
            if (GlobalPolicy.edit()) {
                cols.push({
                    name: 'actions',
                    label: '',
                    sort: false,
                    default: true,
                    class: 'text-right'
                });
            }
            return cols;
        }
    },
    methods: {
        move(interactive, direction) {
            const item = this.item;
            let index = item.meta.interactives.indexOf(interactive),
                to = direction == 'up' ? index - 1 : index + 2,
                from = direction == 'up' ? index + 1 : index;
            item.meta.interactives.splice(to, 0, interactive)
            item.meta.interactives.splice(from, 1);
            this.$emit('update', item);
        },
        validate() {
            if(this.interactive.title == '' || this.interactive.url == '' || this.interactive.type == '') {
                console.error('Missing required values');
                return false;
            }
            return true;
        },
        edit(index) {
            this.modal = 1;
            this.index = index;
            this.interactive = this.item.meta.interactives[index];
        },
        save() {
            if(!this.validate()) return;
            const item = this.item;
            if (this.index === false) {
                item.meta.interactives.push(this.interactive);
            } else {
                item.meta.interactives.splice(this.index, 1, this.interactive);
            }
            this.$emit('update', item);
            this.cancel();
        },
        drop() {
            if(this.index === false) {
                console.error('You must select an item to delete');
                return;
            }
            const item = this.item;
            item.meta.interactives.splice(this.index, 1);
            this.$emit('update', item);
            this.cancel();
        },
        openDelete(i) {
            this.index = i;
            this.deleting = true;
        },
        cancel() {
            this.interactive = {...interactive};
            this.index = false;
            this.modal = false;
            this.deleting = false;
        }
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        context: {
            type: String,
            required: true,
        }
    },
    components: {
        FormButton, ModalDialog, URLField, SelectField, PaginationTable, TextInput
    }
}
</script>